import React, { useEffect, useState } from 'react';
import { MachineData } from '../types/types';
import { Card } from './UI/Card';
import { ProcessFlowChart } from './charts/ProcessFlowChart';
import { ProductionMetricsChart } from './charts/ProductionMetricsChart';
import { MachineComparisonTable } from './UI/MachineComparisonTable';
import { ResourceUsageBarChart } from './charts/ResourceUsageBarChart';
import { ResourceFlowChart } from './charts/ResourceFlowChart';
import { ResourceEfficiencyChart } from './charts/ResourceEfficiencyChart';
import { formatNumber, formatDecimal } from '../utils/formatters';
import { Activity, Clock, AlertTriangle, BarChart3, Zap, Droplets, Wind, Filter, Calendar, ArrowDownUp, ChevronDown, ChevronUp, Gauge, Fuel, Recycle, Workflow, Thermometer, Package, X, Check, AlertCircle } from 'lucide-react';
interface MainDashboardProps {
  machineData: MachineData[];
}
// Hilfsfunktion zum Formatieren des Datums für die Anzeige
const formatDateForDisplay = (date: Date): string => {
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};
export const MainDashboard: React.FC<MainDashboardProps> = ({
  machineData
}) => {
  // State für Filter und Detailansichten
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month' | 'year' | 'custom'>('day');
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    overview: true,
    process: true,
    metrics: true,
    resources: true,
    resourceEfficiency: true,
    resourceFlow: true,
    comparison: true,
    waste: true
  });
  // State für Filtermodal
  const [showFilterModal, setShowFilterModal] = useState(false);
  // State für benutzerdefinierten Zeitraum
  const [customDateRange, setCustomDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  // State für den tatsächlich angewendeten Zeitraum
  const [appliedDateRange, setAppliedDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 1)),
    endDate: new Date()
  });
  // State für Ressourcenansicht
  const [resourceView, setResourceView] = useState<'chart' | 'flow' | 'efficiency'>('chart');
  // State für die Ansichtsart
  const [viewType, setViewType] = useState<'hourly' | 'total'>('hourly');
  // Aktualisiere den angewendeten Zeitraum basierend auf timeRange
  useEffect(() => {
    const endDate = new Date();
    let startDate = new Date();
    if (timeRange === 'day') {
      startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 1);
    } else if (timeRange === 'week') {
      startDate = new Date(endDate);
      startDate.setDate(endDate.getDate() - 7);
    } else if (timeRange === 'month') {
      startDate = new Date(endDate);
      startDate.setMonth(endDate.getMonth() - 1);
    } else if (timeRange === 'year') {
      startDate = new Date(endDate);
      startDate.setFullYear(endDate.getFullYear() - 1);
    } else if (timeRange === 'custom') {
      startDate = new Date(customDateRange.startDate);
      const customEndDate = new Date(customDateRange.endDate);
      // Wenn das Enddatum gültig ist, verwende es, sonst heute
      if (!isNaN(customEndDate.getTime())) {
        endDate.setTime(customEndDate.getTime());
      }
    }
    setAppliedDateRange({
      startDate,
      endDate
    });
  }, [timeRange, customDateRange]);
  // Anwenden des benutzerdefinierten Filters
  const applyCustomFilter = () => {
    setTimeRange('custom');
    setShowFilterModal(false);
  };
  // Berechne Gesamtkennzahlen
  const calculateMetrics = () => {
    // Hier wird jetzt die Filterung nach Zeitraum berücksichtigt
    // Simuliere unterschiedliche Daten basierend auf dem gewählten Zeitraum
    const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
    const operationalMachines = machineData.filter(m => m.status === 'operational');
    const totalMachines = machineData.length;
    const avgEfficiency = Math.round(machineData.reduce((sum, machine) => sum + machine.metrics.efficiency * timeRangeFactor, 0) / totalMachines);
    const totalWarnings = Math.round(machineData.reduce((sum, machine) => sum + machine.metrics.warnings * (timeRange === 'day' ? 0.5 : timeRange === 'week' ? 1 : 1.5), 0));
    const totalOperationalHours = machineData.reduce((sum, machine) => {
      // Betriebsstunden skalieren basierend auf Zeitraum
      let hoursInTimeRange = machine.metrics.operatingHours;
      if (timeRange === 'day') hoursInTimeRange = Math.round(hoursInTimeRange * 0.1);else if (timeRange === 'week') hoursInTimeRange = Math.round(hoursInTimeRange * 0.4);else if (timeRange === 'month') hoursInTimeRange = machine.metrics.operatingHours;
      return sum + hoursInTimeRange;
    }, 0);
    const avgLoad = Math.round(machineData.reduce((sum, machine) => sum + machine.metrics.currentLoad * (timeRange === 'day' ? 0.9 : timeRange === 'week' ? 1.0 : 1.1), 0) / totalMachines);
    // Berechne den durchschnittlichen Durchsatz (Fässer pro Stunde) mit Zeitraumfaktor
    const totalThroughput = machineData.reduce((sum, machine) => {
      if (machine.status !== 'operational') return sum;
      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
      return sum + Math.round(baseRate * 60);
    }, 0);
    // Berechne die Gesamtfehlerrate mit Zeitraumfaktor
    const avgErrorRate = Math.round(machineData.reduce((sum, machine) => sum + machine.analytics.errorRate * (timeRange === 'day' ? 0.7 : timeRange === 'week' ? 1.0 : 1.3), 0) / totalMachines * 10) / 10;
    // Berechne Ressourcenverbrauch mit Zeitraumfaktor
    const calcResourceUsage = () => {
      let totalStrom = 0;
      let totalWasser = 0;
      let totalSand = 0;
      machineData.forEach(machine => {
        if (machine.status !== 'operational') return;
        const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
        const throughput = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10 * 60;
        // Stromverbrauch
        let stromVerbrauch = 1.0;
        if (machine.type === 'ausbeulanlage') stromVerbrauch = 1.5;else if (machine.type === 'waschanlage') stromVerbrauch = 0.8;else if (machine.type === 'roboterarm') stromVerbrauch = 2.5;else if (machine.type === 'faesserBuerste') stromVerbrauch = 0.6;else if (machine.type === 'sandstrahlmaschine') stromVerbrauch = 1.2;else if (machine.type === 'buersteMaschine') stromVerbrauch = 0.7;else if (machine.type === 'dichtpruefungsmaschine') stromVerbrauch = 0.5;else if (machine.type === 'trocknung') stromVerbrauch = 1.8;else if (machine.type === 'signiermaschine') stromVerbrauch = 0.3;
        totalStrom += stromVerbrauch * efficiencyFactor * throughput;
        // Wasserverbrauch
        if (machine.type === 'waschanlage') {
          totalWasser += 12 * efficiencyFactor * throughput;
        } else if (machine.type === 'faesserBuerste') {
          totalWasser += 2 * efficiencyFactor * throughput;
        }
        // Sandverbrauch
        if (machine.type === 'sandstrahlmaschine') {
          totalSand += 3.5 * efficiencyFactor * throughput;
        }
      });
      return {
        stromProStunde: Math.round(totalStrom * 10) / 10,
        wasserProStunde: Math.round(totalWasser * 10) / 10,
        sandProStunde: Math.round(totalSand * 10) / 10,
        stromProFass: totalThroughput > 0 ? Math.round(totalStrom / totalThroughput * 100) / 100 : 0,
        wasserProFass: totalThroughput > 0 ? Math.round(totalWasser / totalThroughput * 100) / 100 : 0,
        sandProFass: totalThroughput > 0 ? Math.round(totalSand / totalThroughput * 100) / 100 : 0
      };
    };
    // Berechne Abfalldaten mit Zeitraumfaktor
    const calcWasteProduction = () => {
      let totalAltoel = 0;
      let totalChemikalien = 0;
      let totalSchmierstoffe = 0;
      let totalSand = 0;
      machineData.forEach(machine => {
        if (machine.status !== 'operational') return;
        const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
        const throughput = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10 * 60;
        // Abfallproduktion je nach Maschinentyp
        if (machine.type === 'waschanlage') {
          totalChemikalien += 0.15 * efficiencyFactor * throughput;
          totalSchmierstoffe += 0.08 * efficiencyFactor * throughput;
        } else if (machine.type === 'ausbeulanlage') {
          totalAltoel += 0.05 * efficiencyFactor * throughput;
        } else if (machine.type === 'roboterarm') {
          totalSchmierstoffe += 0.03 * efficiencyFactor * throughput;
        } else if (machine.type === 'sandstrahlmaschine') {
          totalSand += 3.2 * efficiencyFactor * throughput;
        }
      });
      return {
        altoelProStunde: Math.round(totalAltoel * 10) / 10,
        chemikalienProStunde: Math.round(totalChemikalien * 10) / 10,
        schmierstoffeProStunde: Math.round(totalSchmierstoffe * 10) / 10,
        sandProStunde: Math.round(totalSand * 10) / 10,
        altoelProFass: totalThroughput > 0 ? Math.round(totalAltoel / totalThroughput * 1000) / 1000 : 0,
        chemikalienProFass: totalThroughput > 0 ? Math.round(totalChemikalien / totalThroughput * 1000) / 1000 : 0,
        schmierstoffeProFass: totalThroughput > 0 ? Math.round(totalSchmierstoffe / totalThroughput * 1000) / 1000 : 0,
        sandProFass: totalThroughput > 0 ? Math.round(totalSand / totalThroughput * 100) / 100 : 0,
        gesamtAbfallProStunde: Math.round((totalAltoel + totalChemikalien + totalSchmierstoffe + totalSand) * 10) / 10
      };
    };
    const resourceUsage = calcResourceUsage();
    const wasteProduction = calcWasteProduction();
    return {
      operationalPercentage: Math.round(operationalMachines.length / totalMachines * 100 * (timeRange === 'day' ? 0.95 : timeRange === 'week' ? 1.0 : 1.05)),
      avgEfficiency,
      totalWarnings,
      totalOperationalHours,
      avgLoad,
      totalThroughput,
      avgErrorRate,
      resourceUsage,
      wasteProduction
    };
  };
  const metrics = calculateMetrics();
  // Toggle für Abschnitte
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  // Render Abschnittsheader mit Toggle
  const renderSectionHeader = (title: string, icon: React.ReactNode, section: string) => <div className="flex items-center justify-between cursor-pointer py-2" onClick={() => toggleSection(section)} data-id="element-306">
      <div className="flex items-center gap-2" data-id="element-307">
        {icon}
        <h3 className="text-xl font-medium text-gray-200" data-id="element-308">{title}</h3>
      </div>
      {expandedSections[section] ? <ChevronUp className="text-gray-400" size={20} data-id="element-309" /> : <ChevronDown className="text-gray-400" size={20} data-id="element-310" />}
    </div>;
  // Formatiere den aktuellen Zeitraum als Text
  const getTimeRangeText = () => {
    if (timeRange === 'day') return 'Letzten 24 Stunden';
    if (timeRange === 'week') return 'Letzte Woche';
    if (timeRange === 'month') return 'Letzter Monat';
    if (timeRange === 'year') return 'Letztes Jahr';
    if (timeRange === 'custom') {
      return `${formatDateForDisplay(appliedDateRange.startDate)} - ${formatDateForDisplay(appliedDateRange.endDate)}`;
    }
    return '';
  };
  return <div className="space-y-6" data-id="element-311">
      <div className="flex items-center justify-between" data-id="element-312">
        <div data-id="element-313">
          <h2 className="text-2xl font-bold text-white" data-id="element-314">
            Produktionsübersicht
          </h2>
          <p className="text-gray-400" data-id="element-315">
            Gesamtüberblick über alle Maschinen und Prozesse
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-316">
          <button className="flex items-center gap-2 bg-gray-800 rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700" onClick={() => setShowFilterModal(true)} data-id="element-317">
            <Filter size={16} data-id="element-318" />
            Filter
          </button>
        </div>
      </div>
      {/* Anzeige des aktiven Filters */}
      <div className="bg-gray-800/60 rounded-lg border border-gray-700 p-3 flex items-center justify-between" data-id="element-319">
        <div className="flex items-center gap-2" data-id="element-320">
          <Calendar size={18} className="text-blue-400" data-id="element-321" />
          <span className="text-gray-300" data-id="element-322">Zeitraum:</span>
          <span className="font-medium text-white" data-id="element-323">{getTimeRangeText()}</span>
        </div>
        <div className="flex items-center gap-3" data-id="element-324">
          <div className="flex items-center gap-1" data-id="element-325">
            <div className="w-2 h-2 rounded-full bg-blue-400" data-id="element-326"></div>
            <span className="text-xs text-gray-400" data-id="element-327">
              Daten werden für den gewählten Zeitraum aktualisiert
            </span>
          </div>
          <button className="text-gray-400 hover:text-gray-200" onClick={() => setShowFilterModal(true)} data-id="element-328">
            Ändern
          </button>
        </div>
      </div>
      {/* Filter Modal */}
      {showFilterModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-329">
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 w-full max-w-md shadow-lg" data-id="element-330">
            <div className="flex items-center justify-between mb-4" data-id="element-331">
              <h3 className="text-lg font-medium text-white" data-id="element-332">
                Zeitraum filtern
              </h3>
              <button className="text-gray-400 hover:text-white" onClick={() => setShowFilterModal(false)} data-id="element-333">
                <X size={20} data-id="element-334" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-335">
              <div className="grid grid-cols-2 gap-4" data-id="element-336">
                <button className={`p-3 rounded-lg border ${timeRange === 'day' ? 'border-blue-500 bg-blue-500/20 text-blue-400' : 'border-gray-700 bg-gray-700/50 text-gray-300 hover:bg-gray-700'}`} onClick={() => {
              setTimeRange('day');
              setShowFilterModal(false);
            }} data-id="element-337">
                  <div className="flex flex-col items-center" data-id="element-338">
                    <Clock size={24} className={timeRange === 'day' ? 'text-blue-400' : 'text-gray-400'} data-id="element-339" />
                    <span className="mt-2" data-id="element-340">Tag</span>
                  </div>
                </button>
                <button className={`p-3 rounded-lg border ${timeRange === 'week' ? 'border-blue-500 bg-blue-500/20 text-blue-400' : 'border-gray-700 bg-gray-700/50 text-gray-300 hover:bg-gray-700'}`} onClick={() => {
              setTimeRange('week');
              setShowFilterModal(false);
            }} data-id="element-341">
                  <div className="flex flex-col items-center" data-id="element-342">
                    <Calendar size={24} className={timeRange === 'week' ? 'text-blue-400' : 'text-gray-400'} data-id="element-343" />
                    <span className="mt-2" data-id="element-344">Woche</span>
                  </div>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-345">
                <button className={`p-3 rounded-lg border ${timeRange === 'month' ? 'border-blue-500 bg-blue-500/20 text-blue-400' : 'border-gray-700 bg-gray-700/50 text-gray-300 hover:bg-gray-700'}`} onClick={() => {
              setTimeRange('month');
              setShowFilterModal(false);
            }} data-id="element-346">
                  <div className="flex flex-col items-center" data-id="element-347">
                    <Calendar size={24} className={timeRange === 'month' ? 'text-blue-400' : 'text-gray-400'} data-id="element-348" />
                    <span className="mt-2" data-id="element-349">Monat</span>
                  </div>
                </button>
                <button className={`p-3 rounded-lg border ${timeRange === 'year' ? 'border-blue-500 bg-blue-500/20 text-blue-400' : 'border-gray-700 bg-gray-700/50 text-gray-300 hover:bg-gray-700'}`} onClick={() => {
              setTimeRange('year');
              setShowFilterModal(false);
            }} data-id="element-350">
                  <div className="flex flex-col items-center" data-id="element-351">
                    <Calendar size={24} className={timeRange === 'year' ? 'text-blue-400' : 'text-gray-400'} data-id="element-352" />
                    <span className="mt-2" data-id="element-353">Jahr</span>
                  </div>
                </button>
              </div>
              <div className="grid grid-cols-1" data-id="element-354">
                <button className={`p-3 rounded-lg border ${timeRange === 'custom' ? 'border-blue-500 bg-blue-500/20 text-blue-400' : 'border-gray-700 bg-gray-700/50 text-gray-300 hover:bg-gray-700'}`} onClick={() => {
              setTimeRange('custom');
            }} data-id="element-355">
                  <div className="flex flex-col items-center" data-id="element-356">
                    <Calendar size={24} className={timeRange === 'custom' ? 'text-blue-400' : 'text-gray-400'} data-id="element-357" />
                    <span className="mt-2" data-id="element-358">Benutzerdefiniert</span>
                  </div>
                </button>
              </div>
              {/* Benutzerdefinierter Zeitraum */}
              {timeRange === 'custom' && <div className="mt-4 space-y-4 border-t border-gray-700 pt-4" data-id="element-359">
                  <div data-id="element-360">
                    <label className="block text-sm font-medium text-gray-400 mb-1" data-id="element-361">
                      Startdatum
                    </label>
                    <input type="date" className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white" value={customDateRange.startDate} onChange={e => setCustomDateRange(prev => ({
                ...prev,
                startDate: e.target.value
              }))} data-id="element-362" />
                  </div>
                  <div data-id="element-363">
                    <label className="block text-sm font-medium text-gray-400 mb-1" data-id="element-364">
                      Enddatum
                    </label>
                    <input type="date" className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white" value={customDateRange.endDate} onChange={e => setCustomDateRange(prev => ({
                ...prev,
                endDate: e.target.value
              }))} data-id="element-365" />
                  </div>
                </div>}
              <div className="flex justify-end gap-3 mt-6" data-id="element-366">
                <button className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700" onClick={() => setShowFilterModal(false)} data-id="element-367">
                  Abbrechen
                </button>
                <button className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2" onClick={applyCustomFilter} data-id="element-368">
                  <Check size={18} data-id="element-369" />
                  Anwenden
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Übersicht */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-370">
        {renderSectionHeader('Übersicht', <BarChart3 className="text-blue-400" size={24} data-id="element-371" />, 'overview')}
        {expandedSections.overview && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4" data-id="element-372">
            <Card title="Gesamtdurchsatz" icon={<Activity className="text-green-400" data-id="element-374" />} value={`${formatNumber(metrics.totalThroughput)} Fässer/h`} data-id="element-373" />
            <Card title="Durchschnittliche Effizienz" icon={<BarChart3 className="text-blue-400" data-id="element-376" />} value={`${metrics.avgEfficiency}%`} data-id="element-375" />
            <Card title="Aktive Maschinen" icon={<Clock className="text-purple-400" data-id="element-378" />} value={`${metrics.operationalPercentage}%`} data-id="element-377" />
            <Card title="Offene Warnungen" icon={<AlertTriangle className="text-yellow-400" data-id="element-380" />} value={formatNumber(metrics.totalWarnings)} data-id="element-379" />
          </div>}
      </div>

      {/* Prozessfluss */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-381">
        {renderSectionHeader('Prozessfluss', <Activity className="text-green-400" size={24} data-id="element-382" />, 'process')}
        {expandedSections.process && <div className="mt-4" data-id="element-383">
            <p className="text-gray-300 mb-4" data-id="element-384">
              Visualisierung des Produktionsflusses mit Durchsatzraten und
              aktuellen Status
            </p>
            <div className="h-96" data-id="element-385">
              <ProcessFlowChart machineData={machineData} timeRange={timeRange} dateRange={appliedDateRange} data-id="element-386" />
            </div>
          </div>}
      </div>

      {/* Produktionsmetriken */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-387">
        {renderSectionHeader('Produktionsmetriken', <BarChart3 className="text-blue-400" size={24} data-id="element-388" />, 'metrics')}
        {expandedSections.metrics && <div className="mt-4" data-id="element-389">
            <div className="flex justify-between mb-4" data-id="element-390">
              <div data-id="element-391">
                <h4 className="text-lg font-medium text-gray-200" data-id="element-392">
                  Leistungsvergleich
                </h4>
                <p className="text-gray-400 text-sm" data-id="element-393">
                  Effizienz und Auslastung im Zeitverlauf
                </p>
              </div>
              <div className="flex items-center gap-2" data-id="element-394">
                <div className="flex items-center gap-1" data-id="element-395">
                  <div className="w-3 h-3 rounded-full bg-blue-400" data-id="element-396"></div>
                  <span className="text-sm text-gray-300" data-id="element-397">Effizienz</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-398">
                  <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-399"></div>
                  <span className="text-sm text-gray-300" data-id="element-400">Auslastung</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-401">
                  <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-402"></div>
                  <span className="text-sm text-gray-300" data-id="element-403">Fehlerrate</span>
                </div>
              </div>
            </div>
            <div className="h-80" data-id="element-404">
              <ProductionMetricsChart machineData={machineData} timeRange={timeRange} dateRange={appliedDateRange} data-id="element-405" />
            </div>
          </div>}
      </div>

      {/* Ressourcenverbrauch */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-406">
        {renderSectionHeader('Ressourcenverbrauch', <Zap className="text-yellow-400" size={24} data-id="element-407" />, 'resources')}
        {expandedSections.resources && <div className="mt-4" data-id="element-408">
            <div className="mb-4" data-id="element-409">
              <h4 className="text-lg font-medium text-gray-200" data-id="element-410">
                Gesamtverbrauch im Produktionsprozess
              </h4>
              <p className="text-gray-400 text-sm" data-id="element-411">
                Aktueller Ressourcenverbrauch aller aktiven Maschinen
              </p>
            </div>
            {/* Ressourcenkarten mit verbesserten Daten */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6" data-id="element-412">
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-413">
                <div className="flex items-center gap-2 mb-2" data-id="element-414">
                  <Zap className="text-yellow-400" size={18} data-id="element-415" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-416">
                    Stromverbrauch
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-417">
                  <p className="text-2xl font-bold text-white" data-id="element-418">
                    {formatDecimal(metrics.resourceUsage.stromProFass)} kWh
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-419">
                    {formatDecimal(metrics.resourceUsage.stromProStunde)} kWh/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-420">
                  <p className="text-sm text-gray-400" data-id="element-421">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-422">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-423">
                  <div className="h-1.5 rounded-full bg-yellow-500" style={{
                width: `${Math.min(metrics.resourceUsage.stromProFass * 25, 100)}%`
              }} data-id="element-424"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-425">
                  <div className="flex justify-between" data-id="element-426">
                    <span data-id="element-427">Tagesverbrauch:</span>
                    <span className="font-medium" data-id="element-428">
                      {formatDecimal(metrics.resourceUsage.stromProStunde * 24)}{' '}
                      kWh
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-429">
                    <div data-id="element-430">Höchstverbrauchende Maschine:</div>
                    <div className="font-medium text-blue-400" data-id="element-431">
                      Roboterarm (2.5 kWh/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-432">
                <div className="flex items-center gap-2 mb-2" data-id="element-433">
                  <Droplets className="text-blue-400" size={18} data-id="element-434" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-435">
                    Wasserverbrauch
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-436">
                  <p className="text-2xl font-bold text-white" data-id="element-437">
                    {formatDecimal(metrics.resourceUsage.wasserProFass)} L
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-438">
                    {formatDecimal(metrics.resourceUsage.wasserProStunde)} L/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-439">
                  <p className="text-sm text-gray-400" data-id="element-440">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-441">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-442">
                  <div className="h-1.5 rounded-full bg-blue-500" style={{
                width: `${Math.min(metrics.resourceUsage.wasserProFass * 5, 100)}%`
              }} data-id="element-443"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-444">
                  <div className="flex justify-between" data-id="element-445">
                    <span data-id="element-446">Tagesverbrauch:</span>
                    <span className="font-medium" data-id="element-447">
                      {formatDecimal(metrics.resourceUsage.wasserProStunde * 24)}{' '}
                      L
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-448">
                    <div data-id="element-449">Höchstverbrauchende Maschine:</div>
                    <div className="font-medium text-blue-400" data-id="element-450">
                      Waschanlage (12 L/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-451">
                <div className="flex items-center gap-2 mb-2" data-id="element-452">
                  <Wind className="text-amber-400" size={18} data-id="element-453" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-454">
                    Sandverbrauch
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-455">
                  <p className="text-2xl font-bold text-white" data-id="element-456">
                    {formatDecimal(metrics.resourceUsage.sandProFass)} kg
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-457">
                    {formatDecimal(metrics.resourceUsage.sandProStunde)} kg/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-458">
                  <p className="text-sm text-gray-400" data-id="element-459">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-460">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-461">
                  <div className="h-1.5 rounded-full bg-amber-500" style={{
                width: `${Math.min(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let druckluftVerbrauch = 0;
                  if (machine.type === 'sandstrahlmaschine') druckluftVerbrauch = 15;else if (machine.type === 'dichtpruefungsmaschine') druckluftVerbrauch = 8;
                  return sum + druckluftVerbrauch * efficiencyFactor * throughput;
                }, 0) / metrics.totalThroughput * 5, 100)}%`
              }} data-id="element-462"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-463">
                  <div className="flex justify-between" data-id="element-464">
                    <span data-id="element-465">Tagesverbrauch:</span>
                    <span className="font-medium" data-id="element-466">
                      {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                    const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                    const throughput = Math.round(baseRate * 60);
                    const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                    let druckluftVerbrauch = 0;
                    if (machine.type === 'sandstrahlmaschine') druckluftVerbrauch = 15;else if (machine.type === 'dichtpruefungsmaschine') druckluftVerbrauch = 8;
                    return sum + druckluftVerbrauch * efficiencyFactor * throughput;
                  }, 0) * 24, 1)}{' '}
                      m³
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-467">
                    <div data-id="element-468">Höchstverbrauchende Maschine:</div>
                    <div className="font-medium text-blue-400" data-id="element-469">
                      Sandstrahlmaschine (15 m³/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-470">
                <div className="flex items-center gap-2 mb-2" data-id="element-471">
                  <Wind className="text-blue-300" size={18} data-id="element-472" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-473">
                    Druckluftverbrauch
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-474">
                  <p className="text-2xl font-bold text-white" data-id="element-475">
                    {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let druckluftVerbrauch = 0;
                  if (machine.type === 'sandstrahlmaschine') druckluftVerbrauch = 15;else if (machine.type === 'dichtpruefungsmaschine') druckluftVerbrauch = 8;
                  return sum + druckluftVerbrauch * efficiencyFactor * throughput;
                }, 0) / metrics.totalThroughput, 2)}{' '}
                    m³
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-476">
                    {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let druckluftVerbrauch = 0;
                  if (machine.type === 'sandstrahlmaschine') druckluftVerbrauch = 15;else if (machine.type === 'dichtpruefungsmaschine') druckluftVerbrauch = 8;
                  return sum + druckluftVerbrauch * efficiencyFactor * throughput;
                }, 0), 1)}{' '}
                    m³/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-477">
                  <p className="text-sm text-gray-400" data-id="element-478">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-479">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-480">
                  <div className="h-1.5 rounded-full bg-blue-500" style={{
                width: `${Math.min(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let druckluftVerbrauch = 0;
                  if (machine.type === 'sandstrahlmaschine') druckluftVerbrauch = 15;else if (machine.type === 'dichtpruefungsmaschine') druckluftVerbrauch = 8;
                  return sum + druckluftVerbrauch * efficiencyFactor * throughput;
                }, 0) / metrics.totalThroughput * 5, 100)}%`
              }} data-id="element-481"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-482">
                  <div className="flex justify-between" data-id="element-483">
                    <span data-id="element-484">Tagesverbrauch:</span>
                    <span className="font-medium" data-id="element-485">
                      {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                    const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                    const throughput = Math.round(baseRate * 60);
                    const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                    let druckluftVerbrauch = 0;
                    if (machine.type === 'sandstrahlmaschine') druckluftVerbrauch = 15;else if (machine.type === 'dichtpruefungsmaschine') druckluftVerbrauch = 8;
                    return sum + druckluftVerbrauch * efficiencyFactor * throughput;
                  }, 0) * 24, 1)}{' '}
                      m³
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-486">
                    <div data-id="element-487">Höchstverbrauchende Maschine:</div>
                    <div className="font-medium text-blue-400" data-id="element-488">
                      Sandstrahlmaschine (15 m³/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-489">
                <div className="flex items-center gap-2 mb-2" data-id="element-490">
                  <Droplets className="text-indigo-400" size={18} data-id="element-491" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-492">
                    Hydraulikölverbrauch
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-493">
                  <p className="text-2xl font-bold text-white" data-id="element-494">
                    {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let hydraulikoelVerbrauch = 0;
                  if (machine.type === 'ausbeulanlage') hydraulikoelVerbrauch = 0.2;else if (machine.type === 'roboterarm') hydraulikoelVerbrauch = 0.05;
                  return sum + hydraulikoelVerbrauch * efficiencyFactor * throughput;
                }, 0) / metrics.totalThroughput, 3)}{' '}
                    L
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-495">
                    {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let hydraulikoelVerbrauch = 0;
                  if (machine.type === 'ausbeulanlage') hydraulikoelVerbrauch = 0.2;else if (machine.type === 'roboterarm') hydraulikoelVerbrauch = 0.05;
                  return sum + hydraulikoelVerbrauch * efficiencyFactor * throughput;
                }, 0), 2)}{' '}
                    L/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-496">
                  <p className="text-sm text-gray-400" data-id="element-497">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-498">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-499">
                  <div className="h-1.5 rounded-full bg-indigo-500" style={{
                width: `${Math.min(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let hydraulikoelVerbrauch = 0;
                  if (machine.type === 'ausbeulanlage') hydraulikoelVerbrauch = 0.2;else if (machine.type === 'roboterarm') hydraulikoelVerbrauch = 0.05;
                  return sum + hydraulikoelVerbrauch * efficiencyFactor * throughput;
                }, 0) / metrics.totalThroughput * 250, 100)}%`
              }} data-id="element-500"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-501">
                  <div className="flex justify-between" data-id="element-502">
                    <span data-id="element-503">Tagesverbrauch:</span>
                    <span className="font-medium" data-id="element-504">
                      {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                    const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                    const throughput = Math.round(baseRate * 60);
                    const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                    let hydraulikoelVerbrauch = 0;
                    if (machine.type === 'ausbeulanlage') hydraulikoelVerbrauch = 0.2;else if (machine.type === 'roboterarm') hydraulikoelVerbrauch = 0.05;
                    return sum + hydraulikoelVerbrauch * efficiencyFactor * throughput;
                  }, 0) * 24, 2)}{' '}
                      L
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-505">
                    <div data-id="element-506">Höchstverbrauchende Maschine:</div>
                    <div className="font-medium text-blue-400" data-id="element-507">
                      Ausbeulanlage (0.2 L/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-508">
                <div className="flex items-center gap-2 mb-2" data-id="element-509">
                  <Package className="text-green-400" size={18} data-id="element-510" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-511">
                    Reinigungsmittel
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-512">
                  <p className="text-2xl font-bold text-white" data-id="element-513">
                    {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let reinigungsmittelVerbrauch = 0;
                  if (machine.type === 'waschanlage') reinigungsmittelVerbrauch = 0.3;
                  return sum + reinigungsmittelVerbrauch * efficiencyFactor * throughput;
                }, 0) / metrics.totalThroughput, 3)}{' '}
                    L
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-514">
                    {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let reinigungsmittelVerbrauch = 0;
                  if (machine.type === 'waschanlage') reinigungsmittelVerbrauch = 0.3;
                  return sum + reinigungsmittelVerbrauch * efficiencyFactor * throughput;
                }, 0), 2)}{' '}
                    L/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-515">
                  <p className="text-sm text-gray-400" data-id="element-516">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-517">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-518">
                  <div className="h-1.5 rounded-full bg-green-500" style={{
                width: `${Math.min(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let reinigungsmittelVerbrauch = 0;
                  if (machine.type === 'waschanlage') reinigungsmittelVerbrauch = 0.3;
                  return sum + reinigungsmittelVerbrauch * efficiencyFactor * throughput;
                }, 0) / metrics.totalThroughput * 200, 100)}%`
              }} data-id="element-519"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-520">
                  <div className="flex justify-between" data-id="element-521">
                    <span data-id="element-522">Tagesverbrauch:</span>
                    <span className="font-medium" data-id="element-523">
                      {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                    const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                    const throughput = Math.round(baseRate * 60);
                    const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                    let reinigungsmittelVerbrauch = 0;
                    if (machine.type === 'waschanlage') reinigungsmittelVerbrauch = 0.3;
                    return sum + reinigungsmittelVerbrauch * efficiencyFactor * throughput;
                  }, 0) * 24, 2)}{' '}
                      L
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-524">
                    <div data-id="element-525">Höchstverbrauchende Maschine:</div>
                    <div className="font-medium text-blue-400" data-id="element-526">
                      Waschanlage (0.3 L/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-527">
                <div className="flex items-center gap-2 mb-2" data-id="element-528">
                  <Thermometer className="text-red-400" size={18} data-id="element-529" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-530">
                    Erdgasverbrauch
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-531">
                  <p className="text-2xl font-bold text-white" data-id="element-532">
                    {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let erdgasVerbrauch = 0;
                  if (machine.type === 'trocknung') erdgasVerbrauch = 0.4;
                  return sum + erdgasVerbrauch * efficiencyFactor * throughput;
                }, 0) / metrics.totalThroughput, 3)}{' '}
                    m³
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-533">
                    {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let erdgasVerbrauch = 0;
                  if (machine.type === 'trocknung') erdgasVerbrauch = 0.4;
                  return sum + erdgasVerbrauch * efficiencyFactor * throughput;
                }, 0), 2)}{' '}
                    m³/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-534">
                  <p className="text-sm text-gray-400" data-id="element-535">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-536">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-537">
                  <div className="h-1.5 rounded-full bg-red-500" style={{
                width: `${Math.min(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                  const throughput = Math.round(baseRate * 60);
                  const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                  let erdgasVerbrauch = 0;
                  if (machine.type === 'trocknung') erdgasVerbrauch = 0.4;
                  return sum + erdgasVerbrauch * efficiencyFactor * throughput;
                }, 0) / metrics.totalThroughput * 150, 100)}%`
              }} data-id="element-538"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-539">
                  <div className="flex justify-between" data-id="element-540">
                    <span data-id="element-541">Tagesverbrauch:</span>
                    <span className="font-medium" data-id="element-542">
                      {formatDecimal(machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                    const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10) / 10;
                    const throughput = Math.round(baseRate * 60);
                    const efficiencyFactor = (100 - machine.metrics.efficiency) / 100 + 0.5;
                    let erdgasVerbrauch = 0;
                    if (machine.type === 'trocknung') erdgasVerbrauch = 0.4;
                    return sum + erdgasVerbrauch * efficiencyFactor * throughput;
                  }, 0) * 24, 2)}{' '}
                      m³
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-543">
                    <div data-id="element-544">Höchstverbrauchende Maschine:</div>
                    <div className="font-medium text-blue-400" data-id="element-545">
                      Trocknung (0.4 m³/Fass)
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Ressourcenverbrauch nach Maschine - Tabelle */}
            <div className="bg-gray-700/30 p-4 rounded-xl mb-6 border border-gray-600/30" data-id="element-546">
              <div className="flex items-center justify-between mb-3" data-id="element-547">
                <div className="flex items-center gap-2" data-id="element-548">
                  <BarChart3 className="text-blue-400" size={18} data-id="element-549" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-550">
                    Ressourcenverbrauch nach Maschine
                  </h5>
                </div>
                <div className="flex items-center gap-3" data-id="element-551">
                  <div className="text-sm text-gray-400" data-id="element-552">
                    <select className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white" onChange={e => setViewType(e.target.value as 'hourly' | 'total')} value={viewType} data-id="element-553">
                      <option value="hourly" data-id="element-554">Pro Stunde</option>
                      <option value="total" data-id="element-555">Gesamtmenge im Zeitraum</option>
                    </select>
                  </div>
                  <div className="text-sm text-gray-400" data-id="element-556">
                    {viewType === 'hourly' ? 'Detaillierte Aufschlüsselung pro Stunde' : 'Gesamtverbrauch im gewählten Zeitraum'}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-557">
                <table className="w-full text-sm" data-id="element-558">
                  <thead data-id="element-559">
                    <tr className="border-b border-gray-600" data-id="element-560">
                      <th className="text-left py-2 pr-4 text-gray-300 font-medium" data-id="element-561">
                        Maschine
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-562">
                        Strom {viewType === 'hourly' ? '(kWh/h)' : '(kWh)'}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-563">
                        Wasser {viewType === 'hourly' ? '(L/h)' : '(L)'}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-564">
                        Sand {viewType === 'hourly' ? '(kg/h)' : '(kg)'}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-565">
                        Druckluft {viewType === 'hourly' ? '(m³/h)' : '(m³)'}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-566">
                        Hydrauliköl {viewType === 'hourly' ? '(L/h)' : '(L)'}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-567">
                        Reinigungsmittel{' '}
                        {viewType === 'hourly' ? '(L/h)' : '(L)'}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-568">
                        Erdgas {viewType === 'hourly' ? '(m³/h)' : '(m³)'}
                      </th>
                      <th className="text-right py-2 pl-2 text-gray-300 font-medium" data-id="element-569">
                        Durchsatz{' '}
                        {viewType === 'hourly' ? '(Fässer/h)' : '(Fässer)'}
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-570">
                    {machineData.filter(m => m.status === 'operational').map(machine => {
                  // Zeitraumfaktor für Anpassung der Werte
                  const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                  // Faktor für Gesamtmenge im Zeitraum
                  const totalFactor = timeRange === 'day' ? 24 : timeRange === 'week' ? 168 // 24 * 7
                  : timeRange === 'month' ? 720 // 24 * 30
                  : timeRange === 'year' ? 8760 // 24 * 365
                  : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24));
                  // Berechne Durchsatz für diese Maschine mit Zeitraumfaktor
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                  const throughputHourly = Math.round(baseRate * 60);
                  const throughputTotal = Math.round(throughputHourly * totalFactor);
                  // Berechne Effizienzfaktor
                  const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                  // Standardwerte (0 für nicht verwendete Ressourcen)
                  let stromVerbrauch = 0;
                  let wasserVerbrauch = 0;
                  let sandVerbrauch = 0;
                  let druckluftVerbrauch = 0;
                  let hydraulikoelVerbrauch = 0;
                  let reinigungsmittelVerbrauch = 0;
                  let erdgasVerbrauch = 0;
                  // Maschinenspezifische Verbrauchswerte
                  if (machine.type === 'ausbeulanlage') {
                    stromVerbrauch = 1.5;
                    hydraulikoelVerbrauch = 0.2;
                  } else if (machine.type === 'waschanlage') {
                    stromVerbrauch = 0.8;
                    wasserVerbrauch = 12;
                    reinigungsmittelVerbrauch = 0.3;
                  } else if (machine.type === 'roboterarm') {
                    stromVerbrauch = 2.5;
                    hydraulikoelVerbrauch = 0.05; // Schmiermittel
                  } else if (machine.type === 'faesserBuerste') {
                    stromVerbrauch = 0.6;
                    wasserVerbrauch = 2;
                  } else if (machine.type === 'sandstrahlmaschine') {
                    stromVerbrauch = 1.2;
                    sandVerbrauch = 3.5;
                    druckluftVerbrauch = 15;
                  } else if (machine.type === 'buersteMaschine') {
                    stromVerbrauch = 0.7;
                  } else if (machine.type === 'dichtpruefungsmaschine') {
                    stromVerbrauch = 0.5;
                    druckluftVerbrauch = 8;
                  } else if (machine.type === 'trocknung') {
                    stromVerbrauch = 1.8;
                    erdgasVerbrauch = 0.4;
                  } else if (machine.type === 'signiermaschine') {
                    stromVerbrauch = 0.3;
                  }
                  // Berechne Gesamtverbrauch pro Stunde für jede Ressource
                  const stromProStunde = stromVerbrauch * efficiencyFactor * throughputHourly;
                  const wasserProStunde = wasserVerbrauch * efficiencyFactor * throughputHourly;
                  const sandProStunde = sandVerbrauch * efficiencyFactor * throughputHourly;
                  const druckluftProStunde = druckluftVerbrauch * efficiencyFactor * throughputHourly;
                  const hydraulikoelProStunde = hydraulikoelVerbrauch * efficiencyFactor * throughputHourly;
                  const reinigungsmittelProStunde = reinigungsmittelVerbrauch * efficiencyFactor * throughputHourly;
                  const erdgasProStunde = erdgasVerbrauch * efficiencyFactor * throughputHourly;
                  // Berechne Gesamtverbrauch für den Zeitraum
                  const stromGesamt = stromProStunde * totalFactor;
                  const wasserGesamt = wasserProStunde * totalFactor;
                  const sandGesamt = sandProStunde * totalFactor;
                  const druckluftGesamt = druckluftProStunde * totalFactor;
                  const hydraulikoelGesamt = hydraulikoelProStunde * totalFactor;
                  const reinigungsmittelGesamt = reinigungsmittelProStunde * totalFactor;
                  const erdgasGesamt = erdgasProStunde * totalFactor;
                  return <tr key={machine.id} className="border-b border-gray-700/50" data-id="element-571">
                            <td className="py-2 pr-4" data-id="element-572">
                              <div className="flex items-center gap-2" data-id="element-573">
                                <div className={`w-2 h-2 rounded-full ${machine.metrics.efficiency >= 80 ? 'bg-green-400' : 'bg-yellow-400'}`} data-id="element-574"></div>
                                <span className="font-medium text-gray-200" data-id="element-575">
                                  {machine.name}
                                </span>
                              </div>
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-576">
                              {stromVerbrauch > 0 ? formatDecimal(viewType === 'hourly' ? stromProStunde : stromGesamt, 1) : '-'}
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-577">
                              {wasserVerbrauch > 0 ? formatDecimal(viewType === 'hourly' ? wasserProStunde : wasserGesamt, 1) : '-'}
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-578">
                              {sandVerbrauch > 0 ? formatDecimal(viewType === 'hourly' ? sandProStunde : sandGesamt, 1) : '-'}
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-579">
                              {druckluftVerbrauch > 0 ? formatDecimal(viewType === 'hourly' ? druckluftProStunde : druckluftGesamt, 1) : '-'}
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-580">
                              {hydraulikoelVerbrauch > 0 ? formatDecimal(viewType === 'hourly' ? hydraulikoelProStunde : hydraulikoelGesamt, 2) : '-'}
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-581">
                              {reinigungsmittelVerbrauch > 0 ? formatDecimal(viewType === 'hourly' ? reinigungsmittelProStunde : reinigungsmittelGesamt, 2) : '-'}
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-582">
                              {erdgasVerbrauch > 0 ? formatDecimal(viewType === 'hourly' ? erdgasProStunde : erdgasGesamt, 2) : '-'}
                            </td>
                            <td className="text-right py-2 pl-2 font-medium text-white" data-id="element-583">
                              {formatNumber(viewType === 'hourly' ? throughputHourly : throughputTotal)}
                            </td>
                          </tr>;
                })}
                  </tbody>
                  <tfoot data-id="element-584">
                    <tr className="border-t border-gray-600 bg-gray-700/30" data-id="element-585">
                      <td className="py-2 pr-4 font-medium text-gray-200" data-id="element-586">
                        Gesamtsumme
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-587">
                        {formatDecimal(viewType === 'hourly' ? metrics.resourceUsage.stromProStunde : metrics.resourceUsage.stromProStunde * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 1)}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-588">
                        {formatDecimal(viewType === 'hourly' ? metrics.resourceUsage.wasserProStunde : metrics.resourceUsage.wasserProStunde * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 1)}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-589">
                        {formatDecimal(viewType === 'hourly' ? metrics.resourceUsage.sandProStunde : metrics.resourceUsage.sandProStunde * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 1)}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-590">
                        {formatDecimal(viewType === 'hourly' ? machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let druckluftVerbrauch = 0;
                      if (machine.type === 'sandstrahlmaschine') druckluftVerbrauch = 15;else if (machine.type === 'dichtpruefungsmaschine') druckluftVerbrauch = 8;
                      return sum + druckluftVerbrauch * efficiencyFactor * throughput;
                    }, 0) : machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let druckluftVerbrauch = 0;
                      if (machine.type === 'sandstrahlmaschine') druckluftVerbrauch = 15;else if (machine.type === 'dichtpruefungsmaschine') druckluftVerbrauch = 8;
                      return sum + druckluftVerbrauch * efficiencyFactor * throughput;
                    }, 0) * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 1)}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-591">
                        {formatDecimal(viewType === 'hourly' ? machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let hydraulikoelVerbrauch = 0;
                      if (machine.type === 'ausbeulanlage') hydraulikoelVerbrauch = 0.2;else if (machine.type === 'roboterarm') hydraulikoelVerbrauch = 0.05;
                      return sum + hydraulikoelVerbrauch * efficiencyFactor * throughput;
                    }, 0) : machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let hydraulikoelVerbrauch = 0;
                      if (machine.type === 'ausbeulanlage') hydraulikoelVerbrauch = 0.2;else if (machine.type === 'roboterarm') hydraulikoelVerbrauch = 0.05;
                      return sum + hydraulikoelVerbrauch * efficiencyFactor * throughput;
                    }, 0) * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 2)}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-592">
                        {formatDecimal(viewType === 'hourly' ? machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let reinigungsmittelVerbrauch = 0;
                      if (machine.type === 'waschanlage') reinigungsmittelVerbrauch = 0.3;
                      return sum + reinigungsmittelVerbrauch * efficiencyFactor * throughput;
                    }, 0) : machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let reinigungsmittelVerbrauch = 0;
                      if (machine.type === 'waschanlage') reinigungsmittelVerbrauch = 0.3;
                      return sum + reinigungsmittelVerbrauch * efficiencyFactor * throughput;
                    }, 0) * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 2)}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-593">
                        {formatDecimal(viewType === 'hourly' ? machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let erdgasVerbrauch = 0;
                      if (machine.type === 'trocknung') erdgasVerbrauch = 0.4;
                      return sum + erdgasVerbrauch * efficiencyFactor * throughput;
                    }, 0) : machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let erdgasVerbrauch = 0;
                      if (machine.type === 'trocknung') erdgasVerbrauch = 0.4;
                      return sum + erdgasVerbrauch * efficiencyFactor * throughput;
                    }, 0) * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 2)}
                      </td>
                      <td className="text-right py-2 pl-2 font-medium text-blue-400" data-id="element-594">
                        {formatNumber(viewType === 'hourly' ? machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let erdgasVerbrauch = 0;
                      if (machine.type === 'trocknung') erdgasVerbrauch = 0.4;
                      return sum + erdgasVerbrauch * efficiencyFactor * throughput;
                    }, 0) : machineData.filter(m => m.status === 'operational').reduce((sum, machine) => {
                      // Zeitraumfaktor für Anpassung der Werte
                      const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                      const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                      const throughput = Math.round(baseRate * 60);
                      const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                      let erdgasVerbrauch = 0;
                      if (machine.type === 'trocknung') erdgasVerbrauch = 0.4;
                      return sum + erdgasVerbrauch * efficiencyFactor * throughput;
                    }, 0) * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>}
      </div>

      {/* Neue Sektion für Abfälle */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-595">
        {renderSectionHeader('Anfallende Abfälle zur Entsorgung', <Recycle className="text-red-400" size={24} data-id="element-596" />, 'waste')}
        {expandedSections.waste && <div className="mt-4" data-id="element-597">
            <div className="mb-4" data-id="element-598">
              <h4 className="text-lg font-medium text-gray-200" data-id="element-599">
                Gesamtabfallaufkommen im Produktionsprozess
              </h4>
              <p className="text-gray-400 text-sm" data-id="element-600">
                Anfallende Abfälle aller aktiven Maschinen zur Entsorgung
              </p>
            </div>
            {/* Abfall-Karten */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6" data-id="element-601">
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-602">
                <div className="flex items-center gap-2 mb-2" data-id="element-603">
                  <Fuel className="text-yellow-300" size={18} data-id="element-604" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-605">Altöl</h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-606">
                  <p className="text-2xl font-bold text-white" data-id="element-607">
                    {formatDecimal(metrics.wasteProduction.altoelProFass, 3)} L
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-608">
                    {formatDecimal(metrics.wasteProduction.altoelProStunde, 2)}{' '}
                    L/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-609">
                  <p className="text-sm text-gray-400" data-id="element-610">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-611">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-612">
                  <div className="h-1.5 rounded-full bg-yellow-500" style={{
                width: `${Math.min(metrics.wasteProduction.altoelProFass * 1000, 100)}%`
              }} data-id="element-613"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-614">
                  <div className="flex justify-between" data-id="element-615">
                    <span data-id="element-616">Tagesentsorgung:</span>
                    <span className="font-medium" data-id="element-617">
                      {formatDecimal(metrics.wasteProduction.altoelProStunde * 24, 2)}{' '}
                      L
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-618">
                    <div data-id="element-619">Hauptverursacher:</div>
                    <div className="font-medium text-blue-400" data-id="element-620">
                      Ausbeulanlage (0.05 L/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-621">
                <div className="flex items-center gap-2 mb-2" data-id="element-622">
                  <Droplets className="text-purple-400" size={18} data-id="element-623" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-624">
                    Säurehaltige Chemikalien
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-625">
                  <p className="text-2xl font-bold text-white" data-id="element-626">
                    {formatDecimal(metrics.wasteProduction.chemikalienProFass, 3)}{' '}
                    L
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-627">
                    {formatDecimal(metrics.wasteProduction.chemikalienProStunde, 2)}{' '}
                    L/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-628">
                  <p className="text-sm text-gray-400" data-id="element-629">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-630">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-631">
                  <div className="h-1.5 rounded-full bg-purple-500" style={{
                width: `${Math.min(metrics.wasteProduction.chemikalienProFass * 400, 100)}%`
              }} data-id="element-632"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-633">
                  <div className="flex justify-between" data-id="element-634">
                    <span data-id="element-635">Tagesentsorgung:</span>
                    <span className="font-medium" data-id="element-636">
                      {formatDecimal(metrics.wasteProduction.chemikalienProStunde * 24, 2)}{' '}
                      L
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-637">
                    <div data-id="element-638">Hauptverursacher:</div>
                    <div className="font-medium text-blue-400" data-id="element-639">
                      Waschanlage (0.15 L/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-640">
                <div className="flex items-center gap-2 mb-2" data-id="element-641">
                  <Fuel className="text-gray-300" size={18} data-id="element-642" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-643">
                    Öle & Schmierstoffe
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-644">
                  <p className="text-2xl font-bold text-white" data-id="element-645">
                    {formatDecimal(metrics.wasteProduction.schmierstoffeProFass, 3)}{' '}
                    L
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-646">
                    {formatDecimal(metrics.wasteProduction.schmierstoffeProStunde, 2)}{' '}
                    L/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-647">
                  <p className="text-sm text-gray-400" data-id="element-648">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-649">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-650">
                  <div className="h-1.5 rounded-full bg-gray-500" style={{
                width: `${Math.min(metrics.wasteProduction.schmierstoffeProFass * 600, 100)}%`
              }} data-id="element-651"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-652">
                  <div className="flex justify-between" data-id="element-653">
                    <span data-id="element-654">Tagesentsorgung:</span>
                    <span className="font-medium" data-id="element-655">
                      {formatDecimal(metrics.wasteProduction.schmierstoffeProStunde * 24, 2)}{' '}
                      L
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-656">
                    <div data-id="element-657">Hauptverursacher:</div>
                    <div className="font-medium text-blue-400" data-id="element-658">
                      Waschanlage (0.08 L/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-659">
                <div className="flex items-center gap-2 mb-2" data-id="element-660">
                  <Wind className="text-amber-400" size={18} data-id="element-661" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-662">
                    Verbrauchter Sand
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-663">
                  <p className="text-2xl font-bold text-white" data-id="element-664">
                    {formatDecimal(metrics.wasteProduction.sandProFass, 2)} kg
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-665">
                    {formatDecimal(metrics.wasteProduction.sandProStunde, 1)}{' '}
                    kg/h
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-666">
                  <p className="text-sm text-gray-400" data-id="element-667">pro Fass</p>
                  <p className="text-sm text-gray-400" data-id="element-668">Gesamt pro Stunde</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-669">
                  <div className="h-1.5 rounded-full bg-amber-500" style={{
                width: `${Math.min(metrics.wasteProduction.sandProFass * 20, 100)}%`
              }} data-id="element-670"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-671">
                  <div className="flex justify-between" data-id="element-672">
                    <span data-id="element-673">Tagesentsorgung:</span>
                    <span className="font-medium" data-id="element-674">
                      {formatDecimal(metrics.wasteProduction.sandProStunde * 24, 1)}{' '}
                      kg
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-675">
                    <div data-id="element-676">Hauptverursacher:</div>
                    <div className="font-medium text-blue-400" data-id="element-677">
                      Sandstrahlmaschine (3.2 kg/Fass)
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-700/40 p-4 rounded-xl border border-gray-600/50 min-w-[260px]" data-id="element-678">
                <div className="flex items-center gap-2 mb-2" data-id="element-679">
                  <Recycle className="text-red-400" size={18} data-id="element-680" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-681">
                    Gesamtabfallmenge
                  </h5>
                </div>
                <div className="flex items-baseline justify-between" data-id="element-682">
                  <p className="text-2xl font-bold text-white" data-id="element-683">
                    {formatDecimal(metrics.wasteProduction.gesamtAbfallProStunde, 1)}{' '}
                    kg/h
                  </p>
                  <p className="text-sm text-blue-400 font-medium" data-id="element-684">
                    {formatDecimal(metrics.wasteProduction.gesamtAbfallProStunde * 24, 1)}{' '}
                    kg/Tag
                  </p>
                </div>
                <div className="flex justify-between mt-1" data-id="element-685">
                  <p className="text-sm text-gray-400" data-id="element-686">Stündlich</p>
                  <p className="text-sm text-gray-400" data-id="element-687">Täglich</p>
                </div>
                <div className="mt-3 w-full bg-gray-600/50 h-1.5 rounded-full" data-id="element-688">
                  <div className="h-1.5 rounded-full bg-red-500" style={{
                width: `${Math.min(metrics.wasteProduction.gesamtAbfallProStunde / 5 * 100, 100)}%`
              }} data-id="element-689"></div>
                </div>
                <div className="mt-3 text-sm text-gray-300" data-id="element-690">
                  <div className="flex justify-between" data-id="element-691">
                    <span data-id="element-692">Wochenentsorgung:</span>
                    <span className="font-medium" data-id="element-693">
                      {formatDecimal(metrics.wasteProduction.gesamtAbfallProStunde * 24 * 7, 1)}{' '}
                      kg
                    </span>
                  </div>
                  <div className="mt-1" data-id="element-694">
                    <div data-id="element-695">Abfallintensivste Maschine:</div>
                    <div className="font-medium text-blue-400" data-id="element-696">
                      Sandstrahlmaschine
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Abfallaufkommen nach Maschine - Tabelle */}
            <div className="bg-gray-700/30 p-4 rounded-xl mb-6 border border-gray-600/30" data-id="element-697">
              <div className="flex items-center justify-between mb-3" data-id="element-698">
                <div className="flex items-center gap-2" data-id="element-699">
                  <AlertCircle className="text-red-400" size={18} data-id="element-700" />
                  <h5 className="text-md font-medium text-gray-200" data-id="element-701">
                    Abfallaufkommen nach Maschine
                  </h5>
                </div>
                <div className="flex items-center gap-3" data-id="element-702">
                  <div className="text-sm text-gray-400" data-id="element-703">
                    <select className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white" onChange={e => setViewType(e.target.value as 'hourly' | 'total')} value={viewType} data-id="element-704">
                      <option value="hourly" data-id="element-705">Pro Stunde</option>
                      <option value="total" data-id="element-706">Gesamtmenge im Zeitraum</option>
                    </select>
                  </div>
                  <div className="text-sm text-gray-400" data-id="element-707">
                    {viewType === 'hourly' ? 'Detaillierte Aufschlüsselung pro Stunde' : 'Gesamtabfall im gewählten Zeitraum'}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-708">
                <table className="w-full text-sm" data-id="element-709">
                  <thead data-id="element-710">
                    <tr className="border-b border-gray-600" data-id="element-711">
                      <th className="text-left py-2 pr-4 text-gray-300 font-medium" data-id="element-712">
                        Maschine
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-713">
                        Altöl {viewType === 'hourly' ? '(L/h)' : '(L)'}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-714">
                        Säurehaltige Chemikalien{' '}
                        {viewType === 'hourly' ? '(L/h)' : '(L)'}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-715">
                        Öle & Schmierstoffe{' '}
                        {viewType === 'hourly' ? '(L/h)' : '(L)'}
                      </th>
                      <th className="text-right py-2 px-2 text-gray-300 font-medium" data-id="element-716">
                        Verbrauchter Sand{' '}
                        {viewType === 'hourly' ? '(kg/h)' : '(kg)'}
                      </th>
                      <th className="text-right py-2 pl-2 text-gray-300 font-medium" data-id="element-717">
                        Durchsatz{' '}
                        {viewType === 'hourly' ? '(Fässer/h)' : '(Fässer)'}
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-718">
                    {machineData.filter(m => m.status === 'operational').map(machine => {
                  // Zeitraumfaktor für Anpassung der Werte
                  const timeRangeFactor = timeRange === 'day' ? 0.8 : timeRange === 'week' ? 1.0 : timeRange === 'month' ? 1.2 : timeRange === 'year' ? 1.4 : 1.1; // custom
                  // Faktor für Gesamtmenge im Zeitraum
                  const totalFactor = timeRange === 'day' ? 24 : timeRange === 'week' ? 168 // 24 * 7
                  : timeRange === 'month' ? 720 // 24 * 30
                  : timeRange === 'year' ? 8760 // 24 * 365
                  : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24));
                  // Berechne Durchsatz für diese Maschine mit Zeitraumfaktor
                  const baseRate = Math.round(machine.metrics.efficiency / 10 * (machine.metrics.currentLoad / 100) * 10 * timeRangeFactor) / 10;
                  const throughputHourly = Math.round(baseRate * 60);
                  const throughputTotal = Math.round(throughputHourly * totalFactor);
                  // Berechne Effizienzfaktor
                  const efficiencyFactor = (100 - machine.metrics.efficiency * timeRangeFactor) / 100 + 0.5;
                  // Standardwerte (0 für nicht erzeugte Abfälle)
                  let altoelMenge = 0;
                  let chemikalienMenge = 0;
                  let schmierstoffeMenge = 0;
                  let sandMenge = 0;
                  // Maschinenspezifische Abfallmengen
                  if (machine.type === 'waschanlage') {
                    chemikalienMenge = 0.15;
                    schmierstoffeMenge = 0.08;
                  } else if (machine.type === 'ausbeulanlage') {
                    altoelMenge = 0.05;
                  } else if (machine.type === 'roboterarm') {
                    schmierstoffeMenge = 0.03;
                  } else if (machine.type === 'sandstrahlmaschine') {
                    sandMenge = 3.2;
                  }
                  // Berechne Gesamtabfall pro Stunde für jede Art
                  const altoelProStunde = altoelMenge * efficiencyFactor * throughputHourly;
                  const chemikalienProStunde = chemikalienMenge * efficiencyFactor * throughputHourly;
                  const schmierstoffeProStunde = schmierstoffeMenge * efficiencyFactor * throughputHourly;
                  const sandProStunde = sandMenge * efficiencyFactor * throughputHourly;
                  // Berechne Gesamtabfall für den Zeitraum
                  const altoelGesamt = altoelProStunde * totalFactor;
                  const chemikalienGesamt = chemikalienProStunde * totalFactor;
                  const schmierstoffeGesamt = schmierstoffeProStunde * totalFactor;
                  const sandGesamt = sandProStunde * totalFactor;
                  return <tr key={machine.id} className="border-b border-gray-700/50" data-id="element-719">
                            <td className="py-2 pr-4" data-id="element-720">
                              <div className="flex items-center gap-2" data-id="element-721">
                                <div className={`w-2 h-2 rounded-full ${machine.metrics.efficiency >= 80 ? 'bg-green-400' : 'bg-yellow-400'}`} data-id="element-722"></div>
                                <span className="font-medium text-gray-200" data-id="element-723">
                                  {machine.name}
                                </span>
                              </div>
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-724">
                              {altoelMenge > 0 ? formatDecimal(viewType === 'hourly' ? altoelProStunde : altoelGesamt, 2) : '-'}
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-725">
                              {chemikalienMenge > 0 ? formatDecimal(viewType === 'hourly' ? chemikalienProStunde : chemikalienGesamt, 2) : '-'}
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-726">
                              {schmierstoffeMenge > 0 ? formatDecimal(viewType === 'hourly' ? schmierstoffeProStunde : schmierstoffeGesamt, 2) : '-'}
                            </td>
                            <td className="text-right py-2 px-2" data-id="element-727">
                              {sandMenge > 0 ? formatDecimal(viewType === 'hourly' ? sandProStunde : sandGesamt, 1) : '-'}
                            </td>
                            <td className="text-right py-2 pl-2 font-medium text-white" data-id="element-728">
                              {formatNumber(viewType === 'hourly' ? throughputHourly : throughputTotal)}
                            </td>
                          </tr>;
                })}
                  </tbody>
                  <tfoot data-id="element-729">
                    <tr className="border-t border-gray-600 bg-gray-700/30" data-id="element-730">
                      <td className="py-2 pr-4 font-medium text-gray-200" data-id="element-731">
                        Gesamtsumme
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-732">
                        {formatDecimal(viewType === 'hourly' ? metrics.wasteProduction.altoelProStunde : metrics.wasteProduction.altoelProStunde * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 2)}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-733">
                        {formatDecimal(viewType === 'hourly' ? metrics.wasteProduction.chemikalienProStunde : metrics.wasteProduction.chemikalienProStunde * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 2)}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-734">
                        {formatDecimal(viewType === 'hourly' ? metrics.wasteProduction.schmierstoffeProStunde : metrics.wasteProduction.schmierstoffeProStunde * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 2)}
                      </td>
                      <td className="text-right py-2 px-2 font-medium text-white" data-id="element-735">
                        {formatDecimal(viewType === 'hourly' ? metrics.wasteProduction.sandProStunde : metrics.wasteProduction.sandProStunde * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))), 1)}
                      </td>
                      <td className="text-right py-2 pl-2 font-medium text-blue-400" data-id="element-736">
                        {formatNumber(viewType === 'hourly' ? metrics.totalThroughput : metrics.totalThroughput * (timeRange === 'day' ? 24 : timeRange === 'week' ? 168 : timeRange === 'month' ? 720 : timeRange === 'year' ? 8760 : 24 * Math.ceil((appliedDateRange.endDate.getTime() - appliedDateRange.startDate.getTime()) / (1000 * 3600 * 24))))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
            {/* Umweltrelevanz-Hinweis */}
            <div className="bg-red-900/20 border border-red-700/30 rounded-lg p-4 flex items-start gap-3" data-id="element-737">
              <AlertCircle className="text-red-400 mt-0.5" size={20} data-id="element-738" />
              <div data-id="element-739">
                <h5 className="font-medium text-red-300 mb-1" data-id="element-740">
                  Umweltrelevante Abfälle
                </h5>
                <p className="text-gray-300 text-sm" data-id="element-741">
                  Die anfallenden Abfälle enthalten umweltgefährdende Substanzen
                  und müssen fachgerecht entsorgt werden. Besonders die
                  säurehaltigen Chemikalien und Altöle unterliegen strengen
                  Umweltauflagen.
                </p>
              </div>
            </div>
          </div>}
      </div>

      {/* Maschinenvergleich */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60" data-id="element-742">
        {renderSectionHeader('Maschinenvergleich', <ArrowDownUp className="text-purple-400" size={24} data-id="element-743" />, 'comparison')}
        {expandedSections.comparison && <div className="mt-4 overflow-x-auto" data-id="element-744">
            <MachineComparisonTable machineData={machineData} data-id="element-745" />
          </div>}
      </div>
    </div>;
};